/** Similarity transform (uniform scale + rotation + translation) from old-sheet points to the new sheet's frame. */
export interface Align { s: number; r: number; tx: number; ty: number }
export type Pt = [number, number];
export const IDENTITY: Align = { s: 1, r: 0, tx: 0, ty: 0 };

/** Canvas-style matrix [a, b, c, d, e, f] for an alignment. */
export function alignMatrix(t: Align): [number, number, number, number, number, number] {
  const c = Math.cos(t.r) * t.s, s = Math.sin(t.r) * t.s;
  return [c, s, -s, c, t.tx, t.ty];
}
export function applyAlign(t: Align, p: Pt): Pt {
  const [a, b, c, d, e, f] = alignMatrix(t);
  return [a * p[0] + c * p[1] + e, b * p[0] + d * p[1] + f];
}
export function invertAlign(t: Align, p: Pt): Pt {
  const x = p[0] - t.tx, y = p[1] - t.ty, c = Math.cos(-t.r) / t.s, s = Math.sin(-t.r) / t.s;
  return [c * x - s * y, s * x + c * y];
}
/** Auto-fit: scale the old page uniformly so its page box fits the new one, centred. */
export function fitAlign(oldSize: { w: number; h: number }, newSize: { w: number; h: number }): Align {
  const s = Math.min(newSize.w / oldSize.w, newSize.h / oldSize.h);
  return { s, r: 0, tx: (newSize.w - oldSize.w * s) / 2, ty: (newSize.h - oldSize.h * s) / 2 };
}
/** Two-point align: old-page points q1,q2 land on new-frame points n1,n2. Null if the points are too close. */
export function twoPointAlign(q1: Pt, q2: Pt, n1: Pt, n2: Pt): Align | null {
  const dq: Pt = [q2[0] - q1[0], q2[1] - q1[1]], dn: Pt = [n2[0] - n1[0], n2[1] - n1[1]];
  const lq = Math.hypot(dq[0], dq[1]), ln = Math.hypot(dn[0], dn[1]);
  if (lq < 1 || ln < 1) return null;
  const s = ln / lq, r = Math.atan2(dn[1], dn[0]) - Math.atan2(dq[1], dq[0]);
  const c = Math.cos(r) * s, sn = Math.sin(r) * s;
  return { s, r, tx: n1[0] - (c * q1[0] - sn * q1[1]), ty: n1[1] - (sn * q1[0] + c * q1[1]) };
}
/**
 * Bluebeam-style colour diff, in place into `out`: ink only on the old sheet -> red, only on the new -> blue,
 * on both -> dark. Each sheet is reduced to "ink amount" (1 - luminance), so antialiasing blends smoothly.
 */
export function colorDiff(oldPx: Uint8ClampedArray, newPx: Uint8ClampedArray, out: Uint8ClampedArray): void {
  for (let i = 0; i < out.length; i += 4) {
    const ao = oldPx[i + 3] / 255, an = newPx[i + 3] / 255;
    const lo = 1 - ao + ao * (oldPx[i] * 0.299 + oldPx[i + 1] * 0.587 + oldPx[i + 2] * 0.114) / 255;
    const ln = 1 - an + an * (newPx[i] * 0.299 + newPx[i + 1] * 0.587 + newPx[i + 2] * 0.114) / 255;
    const dO = 1 - lo, dN = 1 - ln;
    out[i] = 255 * (1 - dN) + 0.5;                       // new ink removes red  -> blue
    out[i + 1] = 255 * (1 - Math.max(dO, dN)) + 0.5;
    out[i + 2] = 255 * (1 - dO) + 0.5;                   // old ink removes blue -> red
    out[i + 3] = 255;
  }
}
